import { Link } from 'react-router-dom';
import { Play, Calendar, Trophy, Users, Zap, Target, ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Navigation } from '@/components/Navigation';
import { useAuth } from '@/hooks/useAuth';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function Index() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main>
      {/* Hero Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-semibold mb-6 tracking-tight leading-tight">
              Wikipedia speedruns.
              <br />
              <span className="text-primary">Compete to be fastest.</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-10 max-w-lg mx-auto leading-relaxed">
              Navigate from one Wikipedia article to another using only links. 
              Race the clock, beat your friends, climb the leaderboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/play">
                <Button size="lg" className="text-base h-12 px-8 gap-2 w-full sm:w-auto">
                  <Play className="w-5 h-5" />
                  Start Playing
                </Button>
              </Link>
              <Link to="/daily">
                <Button variant="outline" size="lg" className="h-12 px-6 gap-2 w-full sm:w-auto">
                  <Calendar className="w-5 h-5" />
                  Today's Challenge
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16 border-t border-border/40">
        <div className="container mx-auto px-4">
          <h2 className="sr-only">Why Play WikiRound</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-accent" />
              </div>
              <h3 className="font-medium mb-2">Beat the Clock</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Race to find the fastest path. Every second counts.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-medium mb-2">Compete Globally</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Compare runs with players worldwide. Can you top the leaderboard?
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-success" />
              </div>
              <h3 className="font-medium mb-2">Challenge Friends</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Share your runs and challenge others to beat your time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 border-t border-border/40">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-semibold mb-10 text-center">How It Works</h2>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="game-card text-center">
              <div className="w-10 h-10 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                1
              </div>
              <h3 className="font-medium mb-2">Choose articles</h3>
              <p className="text-sm text-muted-foreground">
                Pick a start and target article, or go random.
              </p>
            </div>
            <div className="game-card text-center">
              <div className="w-10 h-10 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                2
              </div>
              <h3 className="font-medium mb-2">Navigate links</h3>
              <p className="text-sm text-muted-foreground">
                Click through Wikipedia links to reach your target.
              </p>
            </div>
            <div className="game-card text-center">
              <div className="w-10 h-10 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                3
              </div>
              <h3 className="font-medium mb-2">Beat the best</h3>
              <p className="text-sm text-muted-foreground">
                Compare your time and challenge others to beat it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Game Modes */}
      <section className="py-16 border-t border-border/40">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-semibold mb-10 text-center">Game Modes</h2>
          
          <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <div className="game-card">
              <div className="w-10 h-10 mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-accent" />
              </div>
              <h3 className="font-medium mb-2">Time Attack</h3>
              <p className="text-sm text-muted-foreground">
                Fastest path wins. No click limits, just pure speed.
              </p>
            </div>
            <div className="game-card">
              <div className="w-10 h-10 mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-medium mb-2">Click Challenge</h3>
              <p className="text-sm text-muted-foreground">
                Limited clicks. Find the most efficient route.
              </p>
            </div>
            <div className="game-card">
              <div className="w-10 h-10 mb-4 rounded-full bg-success/10 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-success" />
              </div>
              <h3 className="font-medium mb-2">Leagues</h3>
              <p className="text-sm text-muted-foreground">
                Weekly competitions with friends or public groups.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA for non-users */}
      {!user && (
        <section className="py-16 border-t border-border/40">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-xl font-semibold mb-3">Track your progress</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Create an account to save runs, compete on leaderboards, and challenge friends.
            </p>
            <Link to="/auth">
              <Button variant="outline" size="lg" className="gap-2">
                Create Free Account
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-16 border-t border-border/40">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-semibold mb-10 text-center">Frequently Asked Questions</h2>
          
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible className="space-y-2">
              <AccordionItem value="free" className="border border-border/60 rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Is WikiRound free to play?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes, WikiRound is completely free. You can play unlimited games, join leagues, and compete on leaderboards without paying anything. We show occasional ads to keep the lights on, but you can remove them with a one-time purchase if you prefer an ad-free experience.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="account" className="border border-border/60 rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Do I need an account to play?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  No, you can play immediately without signing up. However, creating a free account lets you save your runs, appear on leaderboards, join leagues, and challenge friends. It takes less than a minute to sign up.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="competition" className="border border-border/60 rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  How does competition work?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Every run you complete can be shared. Other players can attempt the same route and try to beat your time or click count. The Daily Challenge gives everyone the same start and target articles, creating a fair global competition. Leagues let you compete weekly with friends or join public groups.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="wikipedia" className="border border-border/60 rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Why Wikipedia?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Wikipedia is the perfect playground for this game: millions of interconnected articles covering every topic imaginable. Finding unexpected connections between subjects is what makes WikiRound challenging and fun. Plus, you might actually learn something along the way.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="rules" className="border border-border/60 rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Are there any rules?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Simple: you can only click links within Wikipedia articles to navigate. No searching, no using your browser's back button, no external tools. Just you, your knowledge, and your instincts for finding connections.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-border/40">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} WikiRound</p>
        </div>
      </footer>
    </div>
  );
}
